import { useState } from "react";
import AttToken from "../../helpers/attToken";
import { useTranslation } from "react-i18next";
import { RiDeleteBin5Line, RiPencilFill } from "react-icons/ri";
import MUIDataTable, { MUIDataTableOptions } from "mui-datatables";
import { tableOptions } from '../TableHelpers/options';
import { StyledDiv } from "../TableHelpers/TableStyle";
import { toast } from "react-toastify";
import queryClient from "../../service/query";
import { ThemeProvider } from '@mui/material/styles';
import { getMuiTheme } from "../TableHelpers/options";
import NovaNotificacao, { newNotificationType } from "../../service/NovaNotificacao";
import NewNotificationsModal from "./Modal/NewNotificationModal";
import { trackEventMatomo } from "../../helpers/matomo";
import { Button, Skeleton, Stack } from "@mui/material";
import AddCircleIcon from '@mui/icons-material/AddCircle';
import QueryFn from "../../helpers/QueryFn";

const notifService = new NovaNotificacao();
const queryFn = new QueryFn();

export default function NewNotifications() {
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [notificationEdit, setNotificationEdit] = useState<newNotificationType | undefined>(undefined);
  const [isEdit, setIsEdit] = useState<boolean>(false);

  const { t } = useTranslation('translation');

  const { data, isLoading } = queryFn.useFetch('GET_NOTIFICATION_LIST',
    async () => {
      try {
        const token = await AttToken();
        if (token) {
          const response = await notifService.getAll(token);
          return response;
        }
      } catch (err) {
        console.log(err);
      }
    });

  const { mutate } = queryFn.useMutate('DELETE_NOTIFICATION',
    async (value: string) => {
      toast.warn(`${t('notifications.newModificationModal.removingNotificationModel')}`);
      try {
        const token = await AttToken();
        if (token) {
          const response = await notifService.delete(token, value);
          queryClient.resetQueries({ queryKey: ['GET_NOTIFICATION_LIST'] });
          toast.success(`${t('notifications.newModificationModal.notificationRemoved')}`);
          return response;
        }
      } catch (err) {
        console.log(err);
        toast.error(`${t('notifications.newModificationModal.errorRemovingNotification')}`);
      }
    });

  const columns = [
    {
      name: "name",
      label: t('name'),
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "message",
      label: t('message'),
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "id",
      label: t('edit'),
      options: {
        customBodyRender: (value: string) => {
          return (
            <Button
              endIcon={<RiPencilFill />}
              onClick={() => {
                trackEventMatomo('tela de notificação 2.0', 'click', 'button', 'abre modal');
                setIsEdit(true);
                setNotificationEdit(data?.find((elem: newNotificationType) => elem.id === value));
                setOpenModal(!openModal);
              }}
              className="button"
            >

              {t('edit')}
            </Button>
          );
        },
        filter: true,
        sort: false,
      }
    },
    {
      name: "id",
      label: t('delete'),
      options: {
        customBodyRender: (value: string) => {
          return (
            <Button
              endIcon={<RiDeleteBin5Line />}
              onClick={() => {
                trackEventMatomo('tela de notificação 2.0', 'click', 'button', 'deleta');
                mutate(value);
              }}
              className="button is-danger"
            >
              {t('delete')}
            </Button>
          );
        },
        filter: true,
        sort: false,
      }
    },
  ];

  const translatedTextLabels: MUIDataTableOptions = {
    textLabels: {
      body: {
        noMatch: `${t('table.noMatch')}`,
        toolTip: `${t('table.toolTip')}`,
        columnHeaderTooltip: (column: any) =>
          `${t('table.columnHeaderTooltip')} ${column.label}`
      },
      pagination: {
        next: `${t('table.next')}`,
        previous: `${t('table.previous')}`,
        rowsPerPage: `${t('table.rowsPerPage')}`,
        displayRows: `${t('table.displayRows')}`
      },
      toolbar: {
        search: `${t('table.search')}`,
        filterTable: `${t('table.filterTable')}`
      },
      filter: {
        title: `${t('table.title')}`,
        reset: `${t('table.reset')}`
      },
      viewColumns: {
        title: `${t('table.viewColumnsTitle')}`
      },
    },
  };

  return (
    <>
      {openModal && (
        <NewNotificationsModal
          isEdit={isEdit}
          openModal={openModal}
          setOpenModal={setOpenModal}
          notificationEdit={notificationEdit}
          setNotificationEdit={setNotificationEdit}
        />
      )}
      <Stack
        direction="column"
        justifyContent="flex-start"
        alignItems="flex-start"
        spacing={1}
        width={'100%'}
      >
        <Button
          disabled={isLoading}
          endIcon={<AddCircleIcon />}
          className="button is-primary"
          type="button"
          name='add'
          onClick={(event: any) => {
            trackEventMatomo('tela de notificação 2.0', 'click', 'button', 'abre modal');
            event.preventDefault();
            setIsEdit(false);
            setNotificationEdit(undefined);
            setOpenModal(!openModal);
          }}
        >
          {t('notifications.newModificationModal.newNotificationModelPlus').slice(0, -1)}
        </Button>
        <Stack
          justifyContent="flex-start"
          alignItems="stretch"
          width={'100%'}
        >
          {isLoading
            ? <Skeleton variant="rounded" width={'100%'} height={400} />
            : <StyledDiv>
              <ThemeProvider theme={getMuiTheme()}>
                <MUIDataTable
                  title={t('notifications.newModificationModal.newNotificationModel')}
                  data={data}
                  columns={columns}
                  options={{ ...tableOptions, ...translatedTextLabels }}
                />
              </ThemeProvider>
            </StyledDiv>
          }
        </Stack>
      </Stack>
    </>
  );
}